import { z } from "zod";

export const CredentialFieldSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  kind: z.enum(["text", "secret", "url"]).default("secret"),
  required: z.boolean().default(true),
  helpText: z.string().default(""),
});
export type CredentialField = z.infer<typeof CredentialFieldSchema>;

export const IntegrationCategorySchema = z.enum([
  "accounting",
  "payments",
  "payroll",
  "pos",
  "communication",
  "supplier",
  "export",
  "other",
]);
export type IntegrationCategory = z.infer<typeof IntegrationCategorySchema>;

// The global catalog (/integrationLibrary/{slug}), seeded by the platform.
// Master admin flips an integration on per tenant; the tenant then just enters
// credentials and is connected — the skuru pattern.
export const IntegrationLibraryEntrySchema = z.object({
  slug: z.string().min(1),
  name: z.string().min(1),
  category: IntegrationCategorySchema,
  description: z.string().default(""),
  authKind: z.enum(["api_key", "oauth", "none"]).default("api_key"),
  credentialFields: z.array(CredentialFieldSchema).default([]),
  docsUrl: z.string().default(""),
  enabled: z.boolean().default(true), // platform-wide kill switch
  order: z.number().int().default(0),
});
export type IntegrationLibraryEntry = z.infer<typeof IntegrationLibraryEntrySchema>;

// Per-tenant state (/tenants/{tid}/integrationConfigs/{slug}).
export const IntegrationConfigSchema = z.object({
  slug: z.string(),
  enabled: z.boolean().default(false),
  // AES-256-GCM blob managed by functions; never readable by clients.
  credentialsEncrypted: z.string().nullable().default(null),
  // Masked, safe-to-display summary, e.g. { apiKey: "••••abcd" }.
  credentialsSummary: z.record(z.string()).default({}),
  connectedAt: z.number().nullable().default(null),
  lastSync: z
    .object({
      at: z.number(),
      ok: z.boolean(),
      message: z.string().default(""),
    })
    .nullable()
    .default(null),
  updatedAt: z.number(),
});
export type IntegrationConfig = z.infer<typeof IntegrationConfigSchema>;

// Mailchimp (/tenants/{tid}/integrationConfigs/mailchimp, field "mailchimp"): the
// company's audience and who goes in. The API key sits in credentialsEncrypted
// like every other integration's credentials.
export const MailchimpSettingsSchema = z.object({
  accountName: z.string().default(""),
  audienceId: z.string().default(""),
  audienceName: z.string().default(""),
  /** New people: subscribed straight away, or pending until they confirm by email (Mailchimp's preference). */
  statusIfNew: z.enum(["pending", "subscribed"]).default("pending"),
  /** Customer types that go in (Homeowner, Builder, Commercial, or the company's own names). */
  types: z.array(z.string().max(40)).max(20).default(["Homeowner"]),
  /** Tags everyone gets, besides their type and lead source. */
  tags: z.array(z.string().max(60)).max(10).default([]),
  /**
   * The sweep picks up customers changed after this mark: the last customer's
   * updatedAt and id, because an import stamps thousands with one updatedAt
   * and a time alone would skip everyone past the page boundary.
   */
  lastSweepAt: z.number().default(0),
  lastSweepId: z.string().default(""),
  lastRun: z
    .object({
      at: z.number(),
      scanned: z.number(),
      added: z.number(),
      updated: z.number(),
      skipped: z.number(),
      failed: z.number(),
      message: z.string().default(""),
    })
    .nullable()
    .default(null),
});
export type MailchimpSettings = z.infer<typeof MailchimpSettingsSchema>;
